import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Switch,
    FormControlLabel,
    Stack,
    Alert,
    Paper,
    Divider,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Fade,
    Grow,
    Container,
    useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import {
    doc,
    getDoc,
    updateDoc,
    query,
    collection,
    where,
    onSnapshot,
    addDoc,
    serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from './firebaseConfig';
import ChatIcon from '@mui/icons-material/Chat';
import GroupIcon from '@mui/icons-material/Group';
import LogoutIcon from '@mui/icons-material/Logout';
import EditIcon from '@mui/icons-material/Edit';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { styled } from '@mui/system';
import { QRCodeSVG } from 'qrcode.react';
import { keyframes } from '@emotion/react';
import AuthCard from './AuthCard'; // Import the AuthCard component

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const WelcomeBox = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    marginBottom: theme.spacing(4),
    animation: `${fadeIn} 1s ease-in`,
}));

const ActionButton = styled(Button)(({ theme }) => ({
    borderRadius: '12px',
    padding: '12px 24px',
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 500,
    boxShadow: 'none',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    },
}));

const StatusSwitch = styled(FormControlLabel)(({ theme }) => ({
    margin: theme.spacing(2, 0),
    '& .MuiSwitch-root': {
        '& .MuiSwitch-switchBase.Mui-checked': {
            color: '#10B981',
        },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#10B981',
        },
    },
}));

const RequestCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: '16px',
    boxShadow:
        '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s',
    '&:hover': {
        transform: 'translateY(-2px)',
    },
}));

const Home = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [userRole, setUserRole] = useState(null);
    const [userStatus, setUserStatus] = useState('inactive');
    const [activeConversation, setActiveConversation] = useState(undefined);
    const [pendingConversation, setPendingConversation] = useState(undefined);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [qrCodeOpen, setQrCodeOpen] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const theme = useTheme();

    const generateOTP = () => {
        return Math.floor(100000 + Math.random() * 900000); // Generates a random 6-digit number
    };

    const handleRequestValet = async () => {
        try {
            const userId = auth.currentUser.uid;
            const conversationRef = await addDoc(
                collection(db, 'conversations'),
                {
                    userId,
                    createdAt: serverTimestamp(),
                    status: 'pending',
                    otp: generateOTP(),
                    carStatus: 'unparked',
                    time_inquiries: 0,
                }
            );

            navigate('/waiting-for-valet', {
                state: { conversationId: conversationRef.id },
            });
        } catch (err) {
            console.error('Error creating a new conversation:', err);
            setError('Failed to send request. Please try again.');
        }
    };

    useEffect(() => {
        let unsubscribeListeners = null;

        const fetchUserData = async (user) => {
            try {
                const userDocRef = doc(db, 'users', user.uid);
                const userDoc = await getDoc(userDocRef);

                if (!userDoc.exists()) {
                    setIsLoading(false);
                    return;
                }

                const userData = userDoc.data();
                const role =
                    userData?.role ??
                    userData?.userRole ??
                    (userData?.isValet ? 'valet' : undefined) ??
                    'customer';

                if (!userData?.role && !userData?.userRole && !userData?.isValet) {
                    console.warn(
                        'User role is missing on Firestore user document. Defaulting role to:',
                        role,
                        'uid:',
                        user.uid,
                        'docId:',
                        userDoc.id,
                        'userData:',
                        userData
                    );
                }

                console.log('User role:', role);
                setUserRole(role);
                setUserStatus(userData.status || 'inactive');

                if (role === 'valet') {
                    console.log('Setting up valet listeners for user:', user.uid);
                    // Valet listeners
                    const pendingRequestsQuery = query(
                        collection(db, 'conversations'),
                        where('status', '==', 'pending')
                    );

                    const unsubscribePending = onSnapshot(
                        pendingRequestsQuery,
                        async (snapshot) => {
                            const requests = [];
                            console.log('Pending requests snapshot received:', snapshot.docs.length, 'documents');
                            console.log('All pending conversations:', snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                            
                            for (const docSnapshot of snapshot.docs) {
                                const data = docSnapshot.data();
                                try {
                                    // Handle guest conversations (no userId field or isGuestConversation flag)
                                    if (data.isGuestConversation || data.customerName === 'Guest' || !data.userId) {
                                        requests.push({
                                            id: docSnapshot.id,
                                            ...data,
                                            customer: {
                                                firstName: data.customerName || 'Guest',
                                                phoneNumber: data.customerPhone || 'N/A',
                                            },
                                            valetName:
                                                data.valetName || 'Valet', // Ensure valetName is set
                                        });
                                    } else {
                                        // Handle regular customer conversations
                                        const customerDocRef = doc(
                                            db,
                                            'users',
                                            data.userId
                                        );
                                        const customerDoc = await getDoc(
                                            customerDocRef
                                        );

                                        requests.push({
                                            id: docSnapshot.id,
                                            ...data,
                                            customer: customerDoc.exists()
                                                ? customerDoc.data()
                                                : null,
                                            valetName:
                                                data.valetName || 'Valet', // Ensure valetName is set
                                        });
                                    }
                                } catch (error) {
                                    console.error(
                                        'Error fetching customer data:',
                                        error
                                    );
                                }
                            }
                            console.log('Final pending requests to display:', requests.length, requests);
                            setPendingRequests(requests);
                            setIsLoading(false); // Clear loading after data is fetched
                        },
                        (error) => {
                            console.error(
                                'Error in pending requests listener:',
                                error
                            );
                            setIsLoading(false);
                        }
                    );

                    const activeQuery = query(
                        collection(db, 'conversations'),
                        where('valetId', '==', user.uid),
                        where('status', '==', 'active')
                    );

                    const unsubscribeActive = onSnapshot(
                        activeQuery,
                        (snapshot) => {
                            const active = snapshot.docs[0];
                            setActiveConversation(
                                active
                                    ? { id: active.id, ...active.data() }
                                    : false
                            );
                        },
                        (error) => {
                            console.error(
                                'Error in active conversation listener:',
                                error
                            );
                            setIsLoading(false);
                        }
                    );

                    unsubscribeListeners = () => {
                        unsubscribePending();
                        unsubscribeActive();
                    };
                } else {
                    // Customer listener
                    const customerQuery = query(
                        collection(db, 'conversations'),
                        where('userId', '==', user.uid)
                    );

                    const unsubscribe = onSnapshot(
                        customerQuery,
                        (snapshot) => {
                            let active = null;
                            let pending = null;

                            snapshot.forEach((docSnapshot) => {
                                const data = docSnapshot.data();
                                if (data.status === 'active') {
                                    active = { id: docSnapshot.id, ...data };
                                } else if (data.status === 'pending') {
                                    pending = { id: docSnapshot.id, ...data };
                                }
                            });

                            setActiveConversation(active || false);
                            setPendingConversation(pending || false);
                            setIsLoading(false); // Clear loading after data is fetched
                        },
                        (error) => {
                            console.error(
                                'Error in customer conversation listener:',
                                error
                            );
                            setIsLoading(false);
                        }
                    );

                    unsubscribeListeners = unsubscribe;
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
                setIsLoading(false);
            }
        };

        const unsubscribeAuth = onAuthStateChanged(
            auth,
            (user) => {
                if (user) {
                    fetchUserData(user);
                } else {
                    setUserRole(null);
                    setUserStatus('inactive');
                    setActiveConversation(false);
                    setPendingConversation(false);
                    setPendingRequests([]);
                    setIsLoading(false);
                    navigate('/');
                }
            },
            (error) => {
                console.error('Auth state change error:', error);
                setIsLoading(false);
            }
        );

        // Cleanup function
        return () => {
            if (unsubscribeListeners) {
                unsubscribeListeners();
            }
            unsubscribeAuth();
        };
    }, [navigate]);

    const handleStatusToggle = async () => {
        if (!auth.currentUser) return;

        const newStatus = userStatus === 'active' ? 'inactive' : 'active';

        try {
            const userDocRef = doc(db, 'users', auth.currentUser.uid);
            await updateDoc(userDocRef, { status: newStatus });
            setUserStatus(newStatus);
        } catch (error) {
            console.error('Error updating user status:', error);
        }
    };

    const handleActiveChat = () => {
        if (activeConversation) {
            navigate('/active-chat', {
                state: { conversationId: activeConversation.id },
            });
        }
    };

    const handleAcceptRequest = async (conversationId) => {
        try {
            const conversationRef = doc(db, 'conversations', conversationId);
            await updateDoc(conversationRef, {
                valetId: auth.currentUser.uid,
                status: 'active',
            });
            navigate('/active-chat', {
                state: { conversationId },
            });
        } catch (error) {
            console.error('Error accepting conversation:', error);
        }
    };

    const handleDenyRequest = async (conversationId) => {
        try {
            const conversationRef = doc(db, 'conversations', conversationId);
            await updateDoc(conversationRef, { status: 'denied' });
            alert('Conversation denied.');
        } catch (error) {
            console.error('Error denying conversation:', error);
        }
    };

    const handleLogout = async () => {
        try {
            if (auth.currentUser && userStatus === 'active') {
                const userDocRef = doc(db, 'users', auth.currentUser.uid);
                await updateDoc(userDocRef, { status: 'inactive' });
            }
            await signOut(auth);
            navigate('/');
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 'calc(100vh - 64px)',
                    width: '100%',
                    position: 'absolute',
                    top: '64px',
                }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%', // Ensure the Box takes the full width
            }}>
            <Container
                maxWidth='sm'
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%', // Ensure the Container takes the full width
                    height: '100%', // Ensure the Container takes the full height
                }}>
                <AuthCard
                    title='Welcome Back!'
                    subtitle={
                        userRole === 'customer'
                            ? activeConversation
                                ? 'You have an active valet service'
                                : pendingConversation
                                ? 'Waiting for valet confirmation...'
                                : 'Find an available valet to get started'
                            : 'Ready to assist customers'
                    }>
                    <Divider sx={{ my: 4 }} />

                    {userRole === 'customer' ? (
                        <Stack spacing={2}>
                            {activeConversation ? (
                                <Grow in={true} timeout={500}>
                                    <ActionButton
                                        variant='contained'
                                        color='primary'
                                        fullWidth
                                        startIcon={<ChatIcon />}
                                        onClick={handleActiveChat}
                                        sx={{ backgroundColor: '#25744f' }}>
                                        View Active Service
                                    </ActionButton>
                                </Grow>
                            ) : pendingConversation ? (
                                <Fade in={true} timeout={500}>
                                    <Alert
                                        severity='info'
                                        sx={{
                                            borderRadius: 3,
                                            backgroundColor: '#F0F9FF',
                                            color: '#0369A1',
                                        }}>
                                        Your request is pending valet acceptance
                                    </Alert>
                                </Fade>
                            ) : (
                                <Box>
                                    <Grow in={true} timeout={500}>
                                        <ActionButton
                                            variant='contained'
                                            fullWidth
                                            startIcon={<GroupIcon />}
                                            onClick={handleRequestValet}
                                            sx={{
                                                backgroundColor: '#10B981',
                                                marginBottom: '2.5%',
                                                width: '90%',
                                                marginLeft: '5%',
                                            }}>
                                            Request a Valet
                                        </ActionButton>
                                    </Grow>
                                    <Grow in={true} timeout={700}>
                                        <ActionButton
                                            variant='contained'
                                            fullWidth
                                            startIcon={<GroupIcon />}
                                            onClick={() =>
                                                navigate('/available-valets')
                                            }
                                            sx={{
                                                backgroundColor: '#10B981',
                                                width: '90%',
                                                marginLeft: '5%',
                                            }}>
                                            Find Available Valets
                                        </ActionButton>
                                    </Grow>
                                </Box>
                            )}
                        </Stack>
                    ) : (
                        <Box>
                            <StatusSwitch
                                control={
                                    <Switch
                                        checked={userStatus === 'active'}
                                        onChange={handleStatusToggle}
                                    />
                                }
                                label={`Status: ${
                                    userStatus === 'active'
                                        ? 'Active'
                                        : 'Inactive'
                                }`}
                            />

                            <Stack spacing={2}>
                                {activeConversation ? (
                                    <Grow in={true} timeout={500}>
                                        <ActionButton
                                            variant='contained'
                                            color='primary'
                                            fullWidth
                                            startIcon={<ChatIcon />}
                                            onClick={handleActiveChat}
                                            sx={{
                                                backgroundColor: '#25744f',
                                            }}>
                                            View Active Service
                                        </ActionButton>
                                    </Grow>
                                ) : null}

                                <Grow in={true} timeout={700}>
                                    <ActionButton
                                        variant='contained'
                                        fullWidth
                                        startIcon={<QrCodeIcon />}
                                        onClick={() => {
                                            setQrCodeOpen(true);
                                        }}
                                        sx={{ backgroundColor: '#10B981' }}>
                                        Generate QR Code
                                    </ActionButton>
                                </Grow>
                            </Stack>

                            <Typography
                                variant='h6'
                                sx={{ mt: 4, mb: 2, color: '#1E293B' }}>
                                Pending Requests
                            </Typography>

                            {pendingRequests.length > 0 ? (
                                pendingRequests.map((request) => (
                                    <Grow
                                        in={true}
                                        timeout={500}
                                        key={request.id}>
                                        <RequestCard>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                    alignItems: 'center',
                                                }}>
                                                <Typography
                                                    sx={{
                                                        color: '#1E293B',
                                                    }}>
                                                    New service request
                                                </Typography>
                                                <Box>
                                                    <ActionButton
                                                        variant='contained'
                                                        sx={{
                                                            mr: 1,
                                                            backgroundColor:
                                                                '#10B981',
                                                        }}
                                                        onClick={() =>
                                                            handleAcceptRequest(
                                                                request.id
                                                            )
                                                        }>
                                                        Accept
                                                    </ActionButton>
                                                    <ActionButton
                                                        variant='outlined'
                                                        color='error'
                                                        onClick={() =>
                                                            handleDenyRequest(
                                                                request.id
                                                            )
                                                        }>
                                                        Decline
                                                    </ActionButton>
                                                </Box>
                                            </Box>
                                        </RequestCard>
                                    </Grow>
                                ))
                            ) : (
                                <Fade in={true} timeout={500}>
                                    <Alert
                                        severity='info'
                                        sx={{
                                            borderRadius: 3,
                                            backgroundColor: '#F0F9FF',
                                            color: '#0369A1',
                                        }}>
                                        No pending requests at the moment
                                    </Alert>
                                </Fade>
                            )}
                        </Box>
                    )}

                    <Divider sx={{ my: 4 }} />

                    <Stack spacing={2}>
                        <Grow in={true} timeout={500}>
                            <ActionButton
                                variant='outlined'
                                fullWidth
                                startIcon={<EditIcon />}
                                onClick={() => navigate('/edit-profile')}
                                sx={{
                                    borderColor: '#E2E8F0',
                                    color: '#64748B',
                                }}>
                                Edit Profile
                            </ActionButton>
                        </Grow>

                        <Grow in={true} timeout={700}>
                            <ActionButton
                                variant='outlined'
                                color='error'
                                fullWidth
                                startIcon={<LogoutIcon />}
                                onClick={handleLogout}>
                                Sign Out
                            </ActionButton>
                        </Grow>
                    </Stack>

                    <Dialog
                        open={qrCodeOpen}
                        onClose={() => setQrCodeOpen(false)}>
                        <DialogTitle>Your QR Code</DialogTitle>
                        <DialogContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    padding: 2,
                                }}>
                                <QRCodeSVG
                                    value={`${process.env.REACT_APP_QR_CODE_URL}/available-valets?valetId=${auth.currentUser?.uid}`}
                                    size={256}
                                />
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setQrCodeOpen(false)}>
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog>
                </AuthCard>
            </Container>
        </Box>
    );
};

export default Home;
